import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { addNotification, notificationStates } from '@components/Notification';
import { useEffectiveHolidays } from '@/hooks/useEffectiveHolidays';
import companyService from '@/services/company';

import { holidayTypes } from './HolidayListEditor.constants';
import { HolidaysSectionView } from './HolidaysSection.view';

const COMPANY_OPTION = { value: null, label: 'Company Defaults' };

export const HolidaysSection = () => {
  const userProfile = useSelector((state) => state.auth.profile);
  const isAdmin = userProfile?.activeCompany?.role === 'Admin';
  const teamsData = useSelector((state) => state.team?.teams?.data ?? []);

  const contextOptions = useMemo(() => {
    const teamOptions = teamsData
      .filter((team) => !team.teamRemoved)
      .map((team) => ({ value: team.activeTeamid, label: team.Team }));
    return [COMPANY_OPTION, ...teamOptions];
  }, [teamsData]);

  const [selectedContext, setSelectedContext] = useState(COMPANY_OPTION);
  const selectedTeamId = selectedContext?.value ?? null;
  const isTeamContext = selectedTeamId != null;

  const { holidays: effectiveHolidays, setOverrides } =
    useEffectiveHolidays(selectedTeamId);

  const [holidays, setHolidays] = useState([]);
  const [isPersisting, setIsPersisting] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState(null);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);

  // Load the effective list (company, or company+team-overrides) into local
  // state whenever it changes (initial fetch, context switch, or a live
  // update from another admin's save).
  useEffect(() => {
    setHolidays(effectiveHolidays);
  }, [effectiveHolidays]);

  const requireAdmin = () => {
    if (isAdmin) return true;
    addNotification({
      message: 'Access denied: Only administrators can manage the holiday list',
      level: notificationStates.error,
    });
    return false;
  };

  // Company-wide write: persists the full list on the company doc. Takes an
  // optional onSaved callback (e.g. close this modal) fired in the same tick
  // as the holidays-list update, not after - otherwise the list update and
  // the modal close land in separate renders, and a still-open modal can
  // re-render in between with its own just-submitted date now present in
  // the list, flagging a false "already exists" against itself.
  const persistHolidays = async (nextHolidays, successMessage, onSaved) => {
    if (!requireAdmin()) return false;

    setIsPersisting(true);
    try {
      const result = await companyService.updateHolidays({
        companyId: userProfile.activeCompany.companyId,
        year: new Date().getFullYear(),
        holidays: nextHolidays,
        updatedBy: userProfile.id,
      });
      if (!result.success) throw new Error(result.error);

      setHolidays(nextHolidays);
      onSaved?.();
      addNotification({
        message: successMessage,
        level: notificationStates.success,
      });
      return true;
    } catch (error) {
      console.error('Error updating holiday list:', error);
      addNotification({
        message: 'Failed to update the holiday list',
        level: notificationStates.error,
      });
      return false;
    } finally {
      setIsPersisting(false);
    }
  };

  // Team-scoped write: touches only this team's override for the given
  // date(s) - the company list and every other team are unaffected. Once a
  // team owns an override for a date, it stays theirs even if the company
  // later edits or removes that date. Takes a { date: holiday|null } map so
  // a single logical edit that spans two dates (e.g. moving a holiday) is
  // one write instead of two. See persistHolidays for why onSaved is fired
  // here rather than by the caller after awaiting.
  const persistTeamOverrides = async (updates, successMessage, onSaved) => {
    if (!requireAdmin()) return false;

    setIsPersisting(true);
    try {
      await setOverrides(updates, userProfile.id);
      onSaved?.();
      addNotification({
        message: successMessage,
        level: notificationStates.success,
      });
      return true;
    } catch (error) {
      console.error('Error updating team holiday override:', error);
      addNotification({
        message: 'Failed to update the holiday list',
        level: notificationStates.error,
      });
      return false;
    } finally {
      setIsPersisting(false);
    }
  };

  const openAddModal = () => {
    setEditingHoliday(null);
    setIsModalOpen(true);
  };

  const openEditModal = (holiday) => {
    setEditingHoliday(holiday);
    setIsModalOpen(true);
  };

  const handleModalSubmit = async (values) => {
    if (isTeamContext) {
      // Moving a holiday to a new date needs two entries in the same write:
      // free the old date and claim the new one.
      const updates =
        editingHoliday && editingHoliday.date !== values.date
          ? { [editingHoliday.date]: null, [values.date]: values }
          : { [values.date]: values };

      await persistTeamOverrides(
        updates,
        editingHoliday ? 'Holiday updated' : 'Holiday added',
        () => setIsModalOpen(false)
      );
      return;
    }

    const nextHolidays = editingHoliday
      ? holidays.map((holiday) =>
          holiday.date === editingHoliday.date ? values : holiday
        )
      : [...holidays, values];

    await persistHolidays(
      nextHolidays,
      editingHoliday ? 'Holiday updated' : 'Holiday added',
      () => setIsModalOpen(false)
    );
  };

  const handleRemove = (date) =>
    isTeamContext
      ? persistTeamOverrides({ [date]: null }, 'Holiday removed')
      : persistHolidays(
          holidays.filter((holiday) => holiday.date !== date),
          'Holiday removed'
        );

  const handleImportRows = async ({ validRows }) => {
    if (!validRows.length) return false;

    const importedHolidays = validRows.map((row) => ({
      date: row.date,
      name: row.name.trim(),
      optional: row.type === holidayTypes.optional,
      description: (row.description || '').trim(),
    }));

    return persistHolidays(
      [...holidays, ...importedHolidays],
      `${importedHolidays.length} holiday${
        importedHolidays.length > 1 ? 's' : ''
      } imported`,
      () => setIsImportModalOpen(false)
    );
  };

  return (
    <HolidaysSectionView
      contextOptions={contextOptions}
      selectedContext={selectedContext}
      onContextChange={setSelectedContext}
      isTeamContext={isTeamContext}
      isPersisting={isPersisting}
      showImportButton={!isTeamContext}
      holidays={holidays}
      onEdit={openEditModal}
      onRemove={handleRemove}
      onAdd={openAddModal}
      isModalOpen={isModalOpen}
      onCloseModal={() => setIsModalOpen(false)}
      onSubmitModal={handleModalSubmit}
      editingHoliday={editingHoliday}
      isImportModalOpen={isImportModalOpen}
      onOpenImportModal={() => setIsImportModalOpen(true)}
      onCloseImportModal={() => setIsImportModalOpen(false)}
      onImportRows={handleImportRows}
    />
  );
};

HolidaysSection.displayName = 'HolidaysSection';
